import React from "react";

import 'react-slideshow-image/dist/styles.css';

import fullRoute from "./../photos/xiao/fullRoute.webp";
import fullyPopulated from "./../photos/xiao/fullyPopulated.webp";
import initTrace from "./../photos/xiao/initTrace.webp";
import milledBoard from "./../photos/xiao/milledBoard.webp";
import paste from "./../photos/xiao/paste.webp";
import rp2040 from "./../photos/xiao/rp2040.webp";
import rpRoute from "./../photos/xiao/rpRoute.webp";
import smallPopulated from "./../photos/xiao/smallPopulated.webp";
import usbc from "./../photos/xiao/usbc.webp";
import withJumpers from "./../photos/xiao/withJumpers.webp";
import demo from "./../photos/xiao/demo.mp4";

import schematic from "./../code/xiao/Motor_Circuit_sch.sch";
import brd from "./../code/xiao/Motor_Circuit_brd.brd";

const Week14 = (props) => {
    return (
        <div className="HP-module">
            <div className="HP-subtitle">Week 14: How to make a XIAO</div>
            <div className="HP-body">
                This week, after working with the XIAO boards all semester, I decided it would be interesting to try and make my 
                own. The goal of the week was to try working with small components, and figure out the challenges and strengths 
                of building my own microcontroller breakout board. For the week, unlike previous weeks, I decided to use the RP2040, 
                mostly since it was what we had available. I started the week off with some helpful advice from Anthony, 
                going over <a href="https://datasheets.raspberrypi.com/rp2040/hardware-design-with-rp2040.pdf">this document</a> detailing 
                how to configure the rp2040. The large challenges with implementing the design would be routing the board efficiently with 
                so many connections and then soldering the board on such a small scale. 
            </div>
            <div className="HP-spacer" />
            <div className="HP-body">
                The first task was to design the board. This was a tall task, since there were so many connections needed to get the chip enabled, 
                and then more to add a <a href="https://www.ti.com/lit/ds/symlink/drv8847.pdf?HQS=dis-dk-null-digikeymode-dsf-pf-null-wwe&ts=1702266677058">
                DRV8847 dual h-bridge</a> so the circuit could drive two DC motors. I designed the board to use a USB-C adapter 
                to provide power to both the circuit and the motors, as well as breaking out a serial connection to open another avenue of communication 
                to the board. Besides these components, I added the DRV8847, flash storage to hold programs, a crystal oscillator to drive the timing on the 
                chip, a 3.3V regulator to provide chip power, a boot and a reset button (boot button connects to the storage, reset to the microcontroller), 
                and a whole host of bypass capacitors on every voltage input/output on the microcontroller and all other I.C.s. This wound up being, while a 
                simple schematic, a very large mess to route. With a lot of creativity, I designed a rather large board which accomplished all of the connections. 
                Here are <a href={schematic} download>the schematic</a> and <a href={brd}>the footprint</a>.
            </div>
            <div className="HP-photoContainer">
                <div className="HP-photoObject">
                    <img src={fullRoute} className="HP-largePhoto"/>
                    <div className="HP-caption">Full footprint of the board</div>
                </div>
                <div className="HP-photoObject">
                    <img src={rpRoute} className="HP-largePhoto"/>
                    <div className="HP-caption">Closeup of the rp2040 routes</div>
                </div>
            </div>
            <div className="HP-body">
                With the circuit designed, now it was time to print it. I used an especially small 8/1000" end-mill as well as the usual 1/64" and 1/32" end mills 
                to get all of the traces routed between the usb-c connector and the rp2040 itself, which both had the tighest pitch between pins. In my exporting to the mill, 
                however, I did forget to mill the through holes on the usb-c, which would later be an issue. I first milled just the outline of the rp2040, checking that the 
                job was setup properly and produced a good cut, then proceeded to mill the entire board.
            </div>
            <div className="HP-photoContainer">
                <div className="HP-photoObject">
                    <img src={initTrace} className="HP-largePhoto"/>
                    <div className="HP-caption">Test cut of the rp2040 section</div>
                </div>
                <div className="HP-photoObject">
                    <img src={milledBoard} className="HP-largePhoto"/>
                    <div className="HP-caption">Fully milled board</div>
                </div>
            </div>
            <div className="HP-body">
                The next large task was to solder all of the components on the board. Since the pins were so closely spaced, and the rp2040 actually didn't have and pins sticking out,
                I needed to use paste and reflow to solder the board. I heavily relied on the microscope and careful paste placement over all the pins to get the rp2040 and the 
                other small components on the board. 
            </div>
            <div className="HP-photoContainer">
                <div className="HP-photoObject">
                    <img src={paste} className="HP-largePhoto"/>
                    <div className="HP-caption">placed solder paste for rp2040, under microscope</div>
                </div>
                <div className="HP-photoObject">
                    <img src={rp2040} className="HP-largePhoto"/>
                    <div className="HP-caption">Fully soldered rp2040, under microscope</div>
                </div>
            </div>
            <div className="HP-body">
                Then I finished the board, here's an image of the final product. 
            </div>
            <div className="HP-photoContainer">
                <div className="HP-photoObject">
                    <img src={smallPopulated} className="HP-largePhoto"/>
                    <div className="HP-caption">All small components placed</div>
                </div>
                <div className="HP-photoObject">
                    <img src={fullyPopulated} className="HP-largePhoto"/>
                    <div className="HP-caption">Rest of components placed</div>
                </div>
            </div>
            <div className="HP-body">
                With the board milled, I plugged it in and tried to turn it on. Using the boot button on plugin, the device should appear over serial connection to be 
                a storage volume. This, however, did not happen. The reason why: Anthony and I had hoped for ease of routing that the 1.1V pins on the rp2040 (the voltage 
                level used for internal logic) did not need to be connected, we were wrong. I made a quick fix with some wires to connect these pins over the board, and 
                then unfortunaltely broke the usb-c connector off the board trying to plug it in (re: use the through hole pins for stability...). The next quick fix for this 
                was another over the board wire solder, this time from the two unused pins on the external power connector to D+ and D-, this way I could use the alternative input 
                to the power regulator as well as the new data pins to connect to usb-a. I used a usb-a breakout, which gives 5v, GND, D+, and D-, to connect to these 4 pins and 
                power the board. The only drawback of this approach was now the usb power no longer powered the motors, I would have to use the alternative power input connector 
                I left on the board to use that functionality. 
            </div>
            <div className="HP-photoContainer">
                <div className="HP-photoObject">
                    <img src={withJumpers} className="HP-largePhoto"/>
                    <div className="HP-caption">Modified board to fix the issues</div>
                </div>
            </div>
            <div className="HP-body">
                After all of these modifications, the rp2040 finally turned on! Over the usb-a connector, the microcontroller appeared visible to the computer as a storage 
                volume. I uploaded the basic echo code from the <a href="https://academy.cba.mit.edu/classes/embedded_programming/index.html">course website</a> and watched my rp2040 talk back to me. 
            </div>
            <div style={{display:"flex", flexDirection:"column", alignItems:"center"}}>
                    <video src={demo} preload="none" controls className="HP-smallVideo" />
                    <div className="HP-caption">rp2040 responding to serial</div>
            </div>
            <div className="HP-body">
                The last thing I tried as part of this week was to fix the usb-c connector that was snapped off earlier. While replacing the original usb-c connector 
                was not a great idea due to the missing through holes, I was able to easily design another tiny breakout board to atleast get power, ground, D+, and D- 
                off the board so I didn't have to use usb-a. I milled the board and attempted to populate it, however once again the soldering process was very difficult and 
                I fused one of the control lines to D+ underneath the connector, rendering it nonfunctioning. At this point I ran out of time, however I hope to fix the connector in the 
                future if I have time.
            </div>
            <div className="HP-photoContainer">
                <div className="HP-photoObject">
                    <img src={usbc} className="HP-largePhoto"/>
                    <div className="HP-caption">USB-C breakout board (broken)</div>
                </div>
            </div>
        </div>
    )
}

export default Week14;